/**
 * Form errors in one shape, whatever the server sent.
 *
 * The API answers with { status, message, errors } either as a 200 response
 * or as an HTTP 4xx/5xx error, and `errors` may hold strings or arrays, keyed
 * by field ("image", "image.0", "variants.2.price"...). readApiErrors() turns
 * any of that into { message, fields: { field: "first message" } }.
 */
export function readApiErrors(source) {
  const data = source?.response?.data ?? source?.data ?? source ?? {};
  const fields = {};
  const raw = data && typeof data === "object" ? data.errors : null;
  if (raw && typeof raw === "object") {
    Object.entries(raw).forEach(([key, value]) => {
      const text = Array.isArray(value) ? value.find(Boolean) : value;
      if (!text) return;
      // "image.0" and "images.3" belong to the photo field; "variants.2.price" to variants.
      const field = String(key).split(".")[0];
      if (!fields[field]) fields[field] = String(text);
    });
  }
  let message = typeof data?.message === "string" ? data.message : "";
  if (source?.response?.status === 413) {
    message = "The upload is larger than the server allows. Use smaller photos.";
    if (!fields.image) fields.image = message;
  }
  if (!message && Object.keys(fields).length) {
    message = "Please fix the highlighted fields.";
  }
  if (!message && source?.message && !source?.response) {
    message = "Could not reach the server. Check the connection and try again.";
  }
  return { message, fields };
}

/** True when the server reply carries form errors (any 4xx status in the body or HTTP). */
export function hasApiErrors(res) {
  const status = Number(res?.data?.status ?? res?.status ?? 0);
  return status >= 400;
}

export const PHOTO_TYPES = ["image/jpeg", "image/png", "image/webp", "image/gif"];
const PHOTO_EXTENSIONS = /\.(jpe?g|png|webp|gif)$/i;

/**
 * Checks a chosen photo before upload. Returns "" when fine, otherwise a
 * message naming the file. Same rules as the server: JPG, PNG, WebP, GIF.
 */
export function checkImageFile(file, { maxMB = 5, types = PHOTO_TYPES, extensions = PHOTO_EXTENSIONS, allowed = "JPG, PNG, WebP or GIF" } = {}) {
  if (!file) return "";
  const name = file.name || "This file";
  const typeOk = file.type ? types.includes(file.type) : extensions.test(name);
  if (!typeOk) {
    const ext = (name.split(".").pop() || "").toUpperCase();
    return `"${name}": ${ext && ext !== name.toUpperCase() ? `${ext} files are` : "this file type is"} not supported. Use a ${allowed} photo.`;
  }
  if (file.size > maxMB * 1024 * 1024) {
    return `"${name}" is ${(file.size / 1024 / 1024).toFixed(1)} MB. Use a photo under ${maxMB} MB.`;
  }
  return "";
}

/** First problem among several files, or "". */
export function checkImageFiles(files, options) {
  for (const file of Array.from(files || [])) {
    const problem = checkImageFile(file, options);
    if (problem) return problem;
  }
  return "";
}

/** Scrolls to and focuses the first field that has an error. */
export function focusFirstError(root = document) {
  window.requestAnimationFrame(() => {
    const el = root.querySelector(".is-invalid, [aria-invalid='true'], .field-error");
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    if (typeof el.focus === "function" && /INPUT|SELECT|TEXTAREA/.test(el.tagName)) el.focus({ preventScroll: true });
  });
}
